import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Network,
  Building2,
  ListTodo,
  MessageSquareText,
  Calendar,
  Megaphone,
  Hash,
  CalendarDays,
  Settings,
  PhoneCall,
  CalendarCheck,
  Users,
  Wallet,
  Radio,
  Trophy,
  Target,
  ClipboardCheck,
  ClipboardList,
  Calculator,
  LineChart,
  BookOpen,
} from 'lucide-react';
import clsx from 'clsx';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { buildQueue, buildSweeperGapList, partitionQueue, laneOf } from '@/domain/queueEngine';
import { useOpsWorklist } from '@/state/useOpsWorklist';
import { confirmationsDueCount } from '@/domain/confirmationWorklist';
import { useConfirmationWorklist } from '@/components/confirmations/useConfirmationWorklist';
import { isMessengerThread, messengerWindow, needsReply } from '@/domain/messengerWindow';
import { buildConversationsFeed } from '@/utils/conversationsFeed';
import { unreadCommsCount } from '@/utils/commsHelpers';
import type { Persona } from '@/domain/types';
import { ALL_STUDIOS_SCOPE } from '@/domain/studioScope';

// IG & FB DMs are the messenger inbox's remit (mirrors MessengerInbox).

type Group = 'studio' | 'queue' | 'bookings' | 'growth' | 'team' | 'accounting' | 'management' | 'system';

// Build status shown as a dot beside each tab. 'ready' = green, safe to test;
// 'wip' = blue, being worked on; 'parked' = red, future work deferred for now.
// Blue (not amber) for wip so it stays easy to tell apart from the red dot.
// Tabs without a status show no dot.
type RailStatus = 'ready' | 'wip' | 'parked';

const STATUS_META: Record<RailStatus, { dot: string; label: string }> = {
  ready: { dot: 'bg-mint', label: 'Ready to test' },
  wip: { dot: 'bg-azure', label: 'Being worked on' },
  parked: { dot: 'bg-blood', label: 'Parked for now' },
};

const STATUS_ORDER: RailStatus[] = ['ready', 'wip', 'parked'];

interface RailLink {
  to: string;
  label: string;
  Icon: typeof Network;
  visible: Persona[];
  group: Group;
  status?: RailStatus;
}

const GROUP_LABELS: Record<Group, string> = {
  studio: 'Studio',
  queue: 'Queue',
  bookings: 'Bookings',
  growth: 'Growth',
  team: 'Team',
  accounting: 'Accounting',
  management: 'Management',
  system: 'System',
};

const GROUP_ORDER: Group[] = [
  'studio',
  'queue',
  'bookings',
  'growth',
  'team',
  'accounting',
  'management',
  'system',
];

// Ordered by the studio-day workflow. STUDIO is the room you're standing in;
// QUEUE is who to contact next; BOOKINGS is the time grid + money-in; GROWTH is
// the pipeline; TEAM is internal; ACCOUNTING is back-office money; MANAGEMENT is
// owner / cross-brand oversight; SYSTEM is demos + settings. Persona gating hides
// empty sections, so each role still sees a short rail.
const LINKS: RailLink[] = [
  // STUDIO — the room you're standing in (Floor/Studio manager)
  {
    to: '/studio/coburg',
    label: 'Studio',
    Icon: Building2,
    visible: ['studio-manager'],
    group: 'studio',
    status: 'ready',
  },
  {
    to: '/availability',
    label: 'Availability',
    Icon: CalendarCheck,
    visible: ['studio-manager', 'artist'],
    group: 'studio',
    status: 'ready',
  },
  {
    to: '/floor',
    label: 'Floor',
    Icon: Radio,
    visible: ['studio-manager', 'floor'],
    group: 'studio',
    status: 'ready',
  },
  {
    to: '/payouts',
    label: 'Payouts & cash',
    Icon: Wallet,
    visible: ['studio-manager'],
    group: 'studio',
    status: 'ready',
  },

  // QUEUE — who to contact next
  {
    to: '/queue',
    label: 'My queue',
    Icon: ListTodo,
    visible: ['bookings-agent'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/sweeper',
    label: 'Sweeper',
    Icon: Target,
    visible: ['georgina', 'sweeper'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/ops',
    label: 'Ops worklist',
    Icon: ClipboardList,
    visible: ['georgina'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/confirmations',
    label: 'Confirmations',
    Icon: PhoneCall,
    visible: ['georgina', 'confirmations'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/conversations',
    label: 'Conversations',
    Icon: MessageSquareText,
    visible: ['messenger'],
    group: 'queue',
    status: 'ready',
  },

  // BOOKINGS — the time grid + money-in
  {
    to: '/calendar/coburg',
    label: 'Calendar',
    Icon: Calendar,
    visible: ['georgina', 'bookings-agent', 'studio-manager', 'confirmations', 'sweeper'],
    group: 'bookings',
    status: 'ready',
  },
  {
    to: '/deposits',
    label: 'Deposits',
    Icon: Wallet,
    visible: ['georgina', 'bookings-agent', 'finance'],
    group: 'bookings',
    status: 'ready',
  },
  {
    to: '/self-book-generator',
    label: 'Self-book',
    Icon: CalendarDays,
    visible: ['artist'],
    group: 'bookings',
    status: 'ready',
  },

  // GROWTH — pipeline / CRM
  {
    to: '/customers',
    label: 'Customers',
    Icon: Users,
    visible: ['bookings-agent', 'studio-manager'],
    group: 'growth',
    status: 'ready',
  },
  {
    to: '/nurture',
    label: 'Nurture',
    Icon: Users,
    visible: ['georgina'],
    group: 'growth',
    status: 'parked',
  },

  // TEAM — internal
  {
    to: '/artists',
    label: 'Artists',
    Icon: Users,
    visible: ['owner', 'georgina'],
    group: 'team',
    status: 'ready',
  },
  {
    to: '/comms',
    label: 'Comms',
    Icon: Hash,
    visible: ['georgina', 'messenger', 'bookings-agent', 'studio-manager', 'artist', 'apprentice', 'confirmations', 'sweeper', 'floor'],
    group: 'team',
    status: 'ready',
  },

  // ACCOUNTING — back-office money (was "Money", amendment 11). The two finance
  // seats (tv-1xc) land here: appending them to the Accounting group only gives
  // them a short finance-only rail, since empty groups are hidden.
  {
    to: '/reconciliation',
    label: 'Reconcile',
    Icon: ClipboardCheck,
    visible: ['finance', 'finance-manager'],
    group: 'accounting',
    status: 'ready',
  },
  {
    to: '/calculations',
    label: 'Day sheet',
    Icon: Calculator,
    visible: ['finance', 'finance-manager'],
    group: 'accounting',
    status: 'ready',
  },

  // MANAGEMENT — owner / cross-brand oversight
  {
    to: '/owner',
    label: 'Owner',
    Icon: Megaphone,
    visible: ['owner'],
    group: 'management',
    status: 'wip',
  },
  {
    to: '/network',
    label: 'Network',
    Icon: Network,
    visible: ['georgina', 'owner'],
    group: 'management',
    status: 'wip',
  },
  {
    to: '/reporting',
    label: 'Reporting',
    Icon: LineChart,
    visible: ['georgina', 'owner'],
    group: 'management',
    status: 'ready',
  },
  {
    to: '/leaderboard',
    label: 'Leaderboard',
    Icon: Trophy,
    visible: [
      'georgina',
      'messenger',
      'bookings-agent',
      'studio-manager',
      'artist',
      'apprentice',
      'finance',
      'finance-manager',
      'owner',
      'confirmations',
      'sweeper',
      'floor',
    ],
    group: 'management',
    status: 'wip',
  },

  // SYSTEM
  {
    to: '/how-it-works',
    label: 'How it works',
    Icon: BookOpen,
    visible: ['owner'],
    group: 'system',
    status: 'ready',
  },
  {
    to: '/settings',
    label: 'Settings',
    Icon: Settings,
    visible: ['owner', 'georgina'],
    group: 'system',
  },
];

export default function LeftRail() {
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const artistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const activeStudio = useSessionStore((s) => s.activeStudio);
  const lastReadByChannel = useSessionStore((s) => s.lastReadByChannel);
  // The Studio & Calendar tabs point at the active studio (the top-bar switcher's
  // selection), so returning to them keeps your scope. On 'all'/unset the Calendar
  // opens its all-studios form (75n8); the Studio room view has no network form,
  // so it falls back to the first assigned studio (and no longer rewrites scope).
  const isNetworkScope = !activeStudio || activeStudio === ALL_STUDIOS_SCOPE;
  const railUrlStudio = !isNetworkScope ? activeStudio : (assignedStudios[0] ?? 'coburg');
  const railCalendarStudio = isNetworkScope ? ALL_STUDIOS_SCOPE : activeStudio;
  const resolveStudioLink = (to: string): string =>
    to.startsWith('/studio/')
      ? `/studio/${railUrlStudio}`
      : to.startsWith('/calendar/')
        ? `/calendar/${railCalendarStudio}`
        : to;
  const railCollapsed = useUIStore((s) => s.railCollapsed);
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const internalComms = useBrainStore((s) => s.internalComms);
  const internalDmThreads = useBrainStore((s) => s.internalDmThreads);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const healingCheckTasks = useBrainStore((s) => s.healingCheckTasks);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const activityFeed = useBrainStore((s) => s.activityFeed);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const selfPersonId =
    persona === 'owner'
      ? agentId ?? 'owner'
      : persona === 'artist'
        ? artistId
        : persona === 'apprentice'
          ? apprenticeId ?? artistId
          : agentId;

  const commsUnread = useMemo(
    () =>
      unreadCommsCount({
        persona,
        agentId,
        selfPersonId,
        channels: internalComms,
        dms: internalDmThreads,
        lastReadByChannel,
      }),
    [persona, agentId, selfPersonId, internalComms, internalDmThreads, lastReadByChannel],
  );

  // --- Rail summary badges: each work surface's whole open total, so its tab
  // shows at a glance how much is waiting behind it. Every count is scoped to
  // the persona's assigned studios (network-wide for Georgina), matching each
  // surface's own screen, and only computed for the persona that sees the link.

  // My queue (bookings agent): items across the queue sequences (First → Reds),
  // i.e. the agent-lane main list — what the seq-tab counts add up to.
  const queueTotal = useMemo(() => {
    if (persona !== 'bookings-agent') return 0;
    const queue = buildQueue({
      customers,
      bookings,
      deposits,
      pieces,
      artists,
      rebookFollowupTasks,
      healingCheckTasks,
      topupPlans,
      sequenceState,
      studioIds: assignedStudios,
      nowIso: clockIso,
    });
    return partitionQueue(queue, clockIso).main.filter((i) => laneOf(i.kind) === 'agent').length;
  }, [
    persona,
    customers,
    bookings,
    deposits,
    pieces,
    artists,
    rebookFollowupTasks,
    healingCheckTasks,
    topupPlans,
    sequenceState,
    assignedStudios,
    clockIso,
  ]);

  // Inbox (messenger): IG/FB threads still holding the client's last word.
  const inboxUnread = useMemo(() => {
    if (persona !== 'messenger') return 0;
    return buildConversationsFeed({ customers, activityFeed, sequenceState, nowIso: clockIso })
      // 7q73: the same rule as the inbox's Needs reply folder.
      .filter((i) => isMessengerThread(i.customer))
      .filter((i) => !i.sentToAgent && needsReply(messengerWindow(i.customer, clockIso)))
      .length;
  }, [persona, customers, activityFeed, sequenceState, clockIso]);

  // Ops worklist (Georgina): whole-desk open total across all seven feeders.
  // 7kta: the SAME derivation as the Ops screen (useOpsWorklist), so the badge
  // equals the page's count.
  const { worklist: opsWorklist } = useOpsWorklist({ enabled: persona === 'georgina' });
  const opsOpenTotal = persona === 'georgina' ? opsWorklist.counts.total : 0;

  // Sweeper (Georgina, or the Sweeper seat): the gaps on the Sweeper rail — 1-2
  // gap artists, this week first, next week once this week is clear (tv-2na.3).
  const sweeperRail = useMemo(() => {
    if (persona !== 'georgina' && persona !== 'sweeper') return { count: 0, week: 'this' as const };
    const { rows, week } = buildSweeperGapList({
      artists,
      bookings,
      nowIso: clockIso,
      studioIds: assignedStudios,
    });
    return { count: rows.length, week };
  }, [persona, artists, bookings, clockIso, assignedStudios]);
  const sweeperGaps = sweeperRail.count;

  // Confirmations (Georgina): the screen's own "due today" figure — the same
  // query + builder as the Confirmations screen (studio scope = assignedStudios,
  // as the screen uses), so the badge always equals the screen (tv-2na.11.2).
  // The due badge sits on the Confirmations link, for whoever works it (Ops, or the Confirmations seat).
  const worksConfirmations = persona === 'georgina' || persona === 'confirmations';
  const { worklist: confirmationWorklist } = useConfirmationWorklist({ enabled: worksConfirmations });
  const confirmationsDue = worksConfirmations ? confirmationsDueCount(confirmationWorklist) : 0;

  if (!persona) return null;

  // C2: artists/apprentices always have a way back to their own dashboard.
  // artists-7: the apprentice's own dashboard is the Artist Dashboard too.
  const selfDashboardTo =
    persona === 'artist' && artistId
      ? `/artist/${artistId}`
      : persona === 'apprentice' && (artistId ?? apprenticeId)
        ? `/artist/${artistId ?? apprenticeId}`
        : null;

  const visibleByGroup = new Map<Group, RailLink[]>();
  for (const link of LINKS) {
    if (!link.visible.includes(persona)) continue;
    const arr = visibleByGroup.get(link.group) ?? [];
    arr.push(link);
    visibleByGroup.set(link.group, arr);
  }

  // A rail link's badge: a whole-surface summary total (calm, uncapped) or the
  // urgent comms-unread ping (blood, 9+ capped). Absent for links without one.
  interface RailBadge {
    count: number;
    testid: string;
    title: string;
    tone: 'summary' | 'urgent';
  }
  const badgeFor = (to: string): RailBadge | null => {
    switch (to) {
      case '/queue':
        return {
          count: queueTotal,
          testid: 'queue-total-badge',
          title: `${queueTotal} ${queueTotal === 1 ? 'item' : 'items'} in your queue`,
          tone: 'summary',
        };
      case '/conversations':
        return {
          count: inboxUnread,
          testid: 'inbox-unread-badge',
          title: `${inboxUnread} unread ${inboxUnread === 1 ? 'thread' : 'threads'} awaiting your reply`,
          tone: 'summary',
        };
      case '/ops':
        return {
          count: opsOpenTotal,
          testid: 'ops-open-total-badge',
          title: `${opsOpenTotal} open on the worklist`,
          tone: 'summary',
        };
      case '/sweeper':
        return {
          count: sweeperGaps,
          testid: 'sweeper-gaps-badge',
          title: `${sweeperGaps} ${sweeperGaps === 1 ? 'gap' : 'gaps'} to fill ${sweeperRail.week === 'next' ? 'next week' : 'this week'}`,
          tone: 'summary',
        };
      case '/confirmations':
        return {
          count: confirmationsDue,
          testid: 'confirmations-due-badge',
          title: `${confirmationsDue} confirmations due today`,
          tone: 'summary',
        };
      case '/comms':
        return {
          count: commsUnread,
          testid: 'comms-unread-badge',
          title: `${commsUnread} unread ${commsUnread === 1 ? 'message' : 'messages'}`,
          tone: 'urgent',
        };
      default:
        return null;
    }
  };

  return (
    <nav
      data-testid="left-rail"
      aria-hidden={railCollapsed || undefined}
      className={`shrink-0 overflow-hidden bg-ink-deep transition-[width] duration-200 ease-out ${
        railCollapsed ? 'w-0 border-r-0' : 'w-56 border-r border-gold-dim/15'
      }`}
    >
      <div
        className={`h-full w-56 py-5 px-3 flex flex-col gap-4 overflow-y-auto transition-opacity duration-200 ${
          railCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {selfDashboardTo ? (
          <NavLink
            to={selfDashboardTo}
            data-testid="rail-my-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-inter transition ${
                isActive
                  ? 'bg-gold-bright/10 text-gold-bright border border-gold-bright/40'
                  : 'text-cream-soft hover:text-cream hover:bg-ink-soft/60 border border-transparent'
              }`
            }
          >
            <Users size={16} />
            <span className="tracking-wide flex-1">My dashboard</span>
          </NavLink>
        ) : null}
        {GROUP_ORDER.map((group) => {
          const items = visibleByGroup.get(group);
          if (!items || items.length === 0) return null;
          return (
            <section key={group} className="flex flex-col gap-0.5">
              <p className="font-inter text-[9px] uppercase tracking-widest text-gold-dim px-3 mb-1">
                {GROUP_LABELS[group]}
              </p>
              {items.map((l) => {
                const badge = badgeFor(l.to);
                const to = resolveStudioLink(l.to);
                return (
                  <NavLink
                    key={l.to}
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-inter transition ${
                        isActive
                          ? 'bg-gold-bright/10 text-gold-bright border border-gold-bright/40'
                          : 'text-cream-soft hover:text-cream hover:bg-ink-soft/60 border border-transparent'
                      }`
                    }
                  >
                    <l.Icon size={16} />
                    <span className="tracking-wide flex-1">{l.label}</span>
                    {badge && badge.count > 0 ? (
                      <span
                        data-testid={badge.testid}
                        className={clsx(
                          'min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-mono font-bold flex items-center justify-center',
                          badge.tone === 'urgent'
                            ? 'bg-blood text-ink-black animate-ring-pulse'
                            : 'bg-gold-bright/20 text-gold-bright',
                        )}
                        title={badge.title}
                      >
                        {badge.tone === 'urgent' && badge.count > 9 ? '9+' : badge.count}
                      </span>
                    ) : null}
                    {l.status ? (
                      <span
                        data-testid={`rail-status-${l.status}`}
                        title={STATUS_META[l.status].label}
                        className={`h-2 w-2 shrink-0 rounded-full ${STATUS_META[l.status].dot}`}
                      />
                    ) : null}
                  </NavLink>
                );
              })}
            </section>
          );
        })}
        <section
          data-testid="rail-status-legend"
          className="mt-auto pt-3 border-t border-gold-dim/15 flex flex-col gap-1.5"
        >
          <p className="font-inter text-[9px] uppercase tracking-widest text-gold-dim px-3 mb-0.5">
            Status
          </p>
          {STATUS_ORDER.map((s) => (
            <div key={s} className="flex items-center gap-2.5 px-3">
              <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_META[s].dot}`} />
              <span className="font-inter text-[11px] text-cream-soft tracking-wide">
                {STATUS_META[s].label}
              </span>
            </div>
          ))}
        </section>
      </div>
    </nav>
  );
}
